import { useLottie } from "lottie-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Dot,
  InnerContainer,
  LottieAnimationWrapper,
  Props,
  StatusContainer,
  StatusText,
  Title,
  text,
} from "./Tile.Parts";

export const Tile = ({
  route,
  title,
  lottieAnimation,
  active = false,
  disabled,
  onKeyDown,
  ref,
  ...rest
}: Props &
  React.HTMLAttributes<HTMLAnchorElement> & {
    ref?: React.Ref<HTMLAnchorElement>;
  }) => {
  const {
    View: AnimatedLottie,
    play,
    setDirection,
    setSpeed,
  } = useLottie({
    animationData: lottieAnimation,
    loop: false,
    autoplay: false,
  });

  const onEnter = useCallback(() => {
    setDirection(1);
    setSpeed(1);
    play();
  }, [play, setDirection, setSpeed]);

  const onLeave = useCallback(() => {
    setDirection(-1);
    setSpeed(3); // Reverse faster
    play();
  }, [play, setDirection, setSpeed]);

  const [isHovered, setIsHovered] = useState(false);
  const shouldBeActive = active || isHovered;
  const prevShouldBeActive = useRef(shouldBeActive);

  useEffect(() => {
    // Only play the animation if the state has changed
    if (shouldBeActive && !prevShouldBeActive.current) {
      onEnter();
    } else if (!shouldBeActive && prevShouldBeActive.current) {
      onLeave();
    }
    // Update the ref for the next render
    prevShouldBeActive.current = shouldBeActive;
  }, [shouldBeActive, onEnter, onLeave]);

  const isExternal = /^https?:\/\//.test(route);

  return (
    <Container
      {...rest}
      as={isExternal ? "a" : Link}
      {...(isExternal ? { href: route } : { to: route })}
      ref={ref}
      onKeyDown={onKeyDown}
      $disabled={disabled}
      $active={active}
      aria-disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
    >
      <InnerContainer $active={active}>
        {disabled && (
          <StatusContainer>
            <Dot />
            <StatusText>{text.activeStatus}</StatusText>
          </StatusContainer>
        )}
        <LottieAnimationWrapper $disabled={disabled}>
          {AnimatedLottie}
        </LottieAnimationWrapper>
        <Title>{title}</Title>
      </InnerContainer>
    </Container>
  );
};
